import type { StripeError } from '@stripe/stripe-js';
import type { ApiErrorModel } from '~/common/models/api.models.ts';
import type { CardPaymentFailureOptions } from '../interfaces/cardPayment.interface';
import { usePaymentError } from './usePaymentError';
import { isOutcomeUnknown } from '../utils/paymentFailure';

export function useCardPaymentFailure(options: CardPaymentFailureOptions) {
  const { $paymentMethods } = useNuxtApp();
  const { reportPaymentError } = usePaymentError();
  const store = useKegelAnalyticStore();
  const { openError } = useAllModals();
  const dialogStore = useDialogStore();
  const { intent, timing } = options;

  const is3dsError = ref(false);

  function stage(): 'Upsale' | 'Subscription' {
    return options.upsale.value ? 'Upsale' : 'Subscription';
  }

  function unlockForm(): void {
    dialogStore.setLoader(false);
    store.isDisabledBaseDialog = false;
  }

  function rejectIncompleteForm(): void {
    openError({ content: 'lp_v7_pop_up_payment_error_message_2' });
    unlockForm();
  }

  function onAcceptError(error: any): void {
    unlockForm();
    reportPaymentError({
      error: error as ApiErrorModel,
      stage: stage(),
      type: $paymentMethods.card,
      errorCode: isOutcomeUnknown(error) ? 'upstream_failure' : undefined,
    });

    if (error?.status === 409) {
      options.onAction();

      return;
    }

    openError({ content: 'lp_v7_pop_up_payment_error_message' });
  }

  function reportCardDecline(stripeError: StripeError | null, error: string): void {
    is3dsError.value = true;
    const formError = String(error).split('code:');
    const errorCode = stripeError?.code || formError[1] || 'card_declined_by_form';
    const declineCode = stripeError?.decline_code;

    reportPaymentError({
      error: declineCode || errorCode,
      errorCode,
      declineCode,
      stage: stage(),
      type: $paymentMethods.card,
    }, { stripe_message: stripeError?.message || formError[0] });

    timing.finishAuthorization();
  }

  async function onCardError(stripeError: StripeError | null, error: string): Promise<void> {
    unlockForm();
    openError({ content: 'lp_v7_pop_up_payment_error_message' });

    reportCardDecline(stripeError, error);
    timing.trackPaymentDuration('error');

    intent.drop();
    options.clearCardFields?.();

    if (options.isLago.value) {
      await options.ensureLagoIntent?.().catch(() => undefined);

      return;
    }

    await intent.load();
  }

  async function handleAuthorizationError(error: unknown): Promise<void> {
    if (is3dsError.value) return;

    timing.finishAccept();
    unlockForm();
    openError({ content: 'lp_v7_pop_up_payment_error_message' });

    reportPaymentError({
      error: error as ApiErrorModel,
      stage: stage(),
      type: $paymentMethods.card,
      errorCode: isOutcomeUnknown(error) ? 'upstream_failure' : undefined,
    });

    timing.trackPaymentDuration('error');

    intent.drop();
    await intent.load();
  }

  return {
    is3dsError,
    unlockForm,
    rejectIncompleteForm,
    onAcceptError,
    onCardError,
    handleAuthorizationError,
  };
}
